/**
 * VENDORED from Cortex-ops src/lib/phi.ts at commit
 * efa253d1686a3677f29f19b7af5c889d2f284097 (2026-09-01). Do not edit by hand:
 * re-copy from Cortex when it changes and bump this pin.
 *
 * The Cortex deep-link builder runs the composed message through these exact
 * screens before it ever becomes a URL, so a message that would be blocked or
 * warned on in Cortex never leaves this app (deep-link design spec, Part B,
 * principle 4).
 */

export const PII_SCREEN_REASON = 'a possible patient name';

const SSN = /(?<!\d)\d{3}-\d{2}-\d{4}(?!\d)/;
const EMAIL = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
// "/" is deliberately not a phone separator so dates like 04/12/1941 never
// read as phone numbers. Digit lookarounds stop matches inside longer runs.
const PHONE = /(?<!\d)(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g;

const DOB_KEYWORD = /\b(?:d\.?\s?o\.?\s?b|date\s+of\s+birth|born)\b/gi;
const DATE =
  /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4}\b|\b\d{1,2}(?:st|nd|rd|th)?\s+(?:of\s+)?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?,?\s+\d{4}\b/gi;
// A DOB keyword and its date can sit a clause apart, so the window is generous.
const DOB_GAP = 48;

const MRN_KEYWORD =
  /\b(?:mrn|chart|record\s+number|patient\s*(?:number|id|no))\b\.?|\b(?:record|patient)\s*#/gi;

const spans = (text, pattern) => {
  const out = [];
  Array.from(text.matchAll(pattern)).forEach((match) => {
    out.push({ start: match.index, end: match.index + match[0].length });
  });
  return out;
};

// True when any keyword match sits within maxGap characters of any target
// match, in either direction (overlapping counts as a gap of zero).
const near = (text, keyword, target, maxGap) => {
  const keywords = spans(text, keyword);
  if (keywords.length === 0) return false;
  const targets = spans(text, target);
  if (targets.length === 0) return false;

  return keywords.some((k) =>
    targets.some((t) => Math.max(k.start - t.end, t.start - k.end, 0) <= maxGap),
  );
};

// Softer "possible PII" heuristics for the live pre-send warning. Warn-only in
// Cortex: they cannot tell a patient name from a staff name. Here any hit
// withholds the link outright, because nobody is present to review it.
const NAMED_PHRASE = /\b(?:patient(?:['’]s)?\s+name\s+is|patient\s+(?:named|called))\s+\S+/i;
const HONORIFIC_NAME = /\b(?:Mr|Mrs|Ms|Miss)\.?\s+[A-Z][a-z]+/;
const STREET_ADDRESS =
  /\b\d{1,6}\s+(?:[A-Z][a-z]{1,15}\s){1,3}(?:[Ss]treet|[Ss]t|[Aa]venue|[Aa]ve|[Rr]oad|[Rr]d|[Bb]oulevard|[Bb]lvd|[Ll]ane|[Ll]n|[Dd]rive|[Dd]r|[Cc]ourt|[Cc]t|[Pp]lace|[Pp]l|[Ww]ay)\b/;
const MEMBER_ID =
  /\b(?:member|policy|subscriber|insurance)\s*(?:id|#|number)?\s*[:#]?\s*(?=[A-Za-z0-9-]*\d)[A-Za-z0-9-]{6,}/i;

// A relation word followed closely by a capitalized full name. Matches whose
// gap contains a preposition ("patient in Valley Radiology") are discarded.
const RELATION_NAME =
  /\b(?:patient|caregiver|wife|husband|spouse|son|daughter|mother|father|mom|dad)\b([^.!?\n]{0,20}?)\s(?:[A-Z][a-z]+(?:\s+(?:[A-Z][a-z]+|Van|De|La|Da|Le|Mac|Mc))+)\b/g;

const relationNameHit = (text) =>
  Array.from(text.matchAll(RELATION_NAME)).some(
    (match) => !/\b(?:in|at|from|to|for|with|the|a|an|of|by|near|on)\b/i.test(match[1]),
  );

const POSSESSIVE_NAME =
  /\b[A-Z][a-z]+(?:\s+(?:[A-Z][a-z]+|Van|De|La|Da|Le))+['’]s\s+(?:chart|visit|appointment|record|file|order|results?)\b/;

export const checkPossiblePII = (text) => {
  if (
    NAMED_PHRASE.test(text) ||
    HONORIFIC_NAME.test(text) ||
    relationNameHit(text) ||
    POSSESSIVE_NAME.test(text)
  ) {
    return 'a patient name';
  }
  if (STREET_ADDRESS.test(text)) return 'a street address';
  if (MEMBER_ID.test(text)) return 'an insurance or member ID';
  return null;
};

// Phone numbers stay blocked as contact details. The one exemption: a *bare*
// run of digits sitting within 12 characters of an MRN/chart keyword is a
// medical record number. A number written with separators is always a contact
// phone, never an MRN, so it blocks even next to the word "chart".
const phoneNotMRN = (text) => {
  const phones = spans(text, PHONE);
  if (phones.length === 0) return false;
  const keywords = spans(text, MRN_KEYWORD);

  return phones.some((phone) => {
    if (!/^\d+$/.test(text.slice(phone.start, phone.end))) return true;
    return !keywords.some((k) => Math.max(k.start - phone.end, phone.start - k.end, 0) <= 12);
  });
};

export const checkPHI = (text) => {
  if (SSN.test(text)) return { blocked: true, reason: 'a Social Security number' };
  if (EMAIL.test(text)) return { blocked: true, reason: 'an email address' };
  if (near(text, DOB_KEYWORD, DATE, DOB_GAP)) return { blocked: true, reason: 'a date of birth' };
  if (phoneNotMRN(text)) return { blocked: true, reason: 'a phone number' };
  return { blocked: false, reason: null };
};

/** The builder's single entry point: hard = Cortex would block, soft = Cortex would warn. */
export const cortexGuard = (text) => {
  const hard = checkPHI(text);
  return { hard: hard.blocked ? hard.reason : null, soft: checkPossiblePII(text) };
};
